import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getFieldValue, RichText } from '@sitecore-jss/sitecore-jss-react';
import CloseIcon from 'components/icons/Close';
import {
  useUiValue,
  setNotificationStatus,
  setNotificationHeight,
} from 'stores/uiContext';
import styled from 'styled-components';
import cx from 'classnames';
import { useMeasure } from 'react-use';
import { useSpring, animated } from 'react-spring';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';
import pageTypes from 'utils/pageTypes';
import { getMobileOS } from 'utils/getMobileOS';

const BannerContainer = styled.div`
  min-height: 3.73rem;
  padding-left: 1.875rem;
  padding-right: 1.875rem;
`;
const RichTextContainer = styled.div`
  overflow-wrap: break-word;
`;

const HIDE_NOTIFICATION = 'hideNotification=';

const isIOSSafari = () => {
  if (typeof navigator === 'undefined') return false;

  return (
    getMobileOS() === 'iOS' &&
    /Safari/i.test(navigator.userAgent) &&
    !/(CriOS|FxiOS|EdgiOS|OPiOS)/i.test(navigator.userAgent)
  );
};

const NotificationBanner = ({ fields = {} }) => {
  let hideNotificationSession = false;

  if (typeof window !== 'undefined' && document) {
    hideNotificationSession =
      typeof document.cookie === 'string' &&
      document.cookie.includes(HIDE_NOTIFICATION + 'true');
  }

  const [measureRef, { height }] = useMeasure();
  const bannerRef = useRef(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  const activeField = getFieldValue(fields, 'active');
  const nBannerText = getFieldValue(fields, 'bannerText');
  const mBText = getFieldValue(fields, 'marketBannerText') || '';
  const mBActive = getFieldValue(fields, 'marketBannerActive') || false;
  const mBAlignment = getFieldValue(fields, 'marketBannerAlignment') || 'left';
  const currentPageType = getFieldValue(fields, 'currentPageType');
  const marketBannerActive =
    mBActive &&
    (currentPageType === pageTypes.CommunityPage ||
      currentPageType === pageTypes.MpcPage ||
      currentPageType === pageTypes.PlanPage ||
      currentPageType === pageTypes.QmiPage ||
      currentPageType === pageTypes.CondoPlan ||
      currentPageType === pageTypes.PromoPage ||
      currentPageType === pageTypes.MarketPage ||
      currentPageType === pageTypes.CondoPage ||
      currentPageType === pageTypes.MarketDesignStudio);

  const textValue = mBActive ? '<p>' + mBText + '</p>' : nBannerText;
  const [
    { notificationActive, hasCommingSoonCommunity, nationalNotificationHeight },
    dispatch,
  ] = useUiValue();

  useEffect(() => {
    dispatch(
      setNotificationStatus(
        (activeField === '1' || marketBannerActive) && !hideNotificationSession,
      ),
    );
  }, []);

  useEffect(() => {
    if (hasCommingSoonCommunity && !marketBannerActive) {
      dispatch(setNotificationStatus(false));
    }
  }, [hasCommingSoonCommunity]);

  const closeNotification = () => {
    dispatch(setNotificationStatus(false));
    document.cookie = HIDE_NOTIFICATION + 'true';
  };

  const [fixedBarVisible, setFixedBarVisible] = useState(false);
  const [visualViewportTop, setVisualViewportTop] = useState(0);
  const isIOS = getMobileOS() === 'iOS';
  const shouldSyncVisualViewportTop = isIOS && !isIOSSafari();

  const setBannerMeasureRef = useCallback(
    (node) => {
      bannerRef.current = node;
      measureRef(node);
    },
    [measureRef],
  );

  const measureBannerHeight = useCallback(() => {
    if (!bannerRef.current) return;

    setBannerHeight(
      Math.ceil(bannerRef.current.getBoundingClientRect().height),
    );
  }, []);

  useEffect(() => {
    if (!shouldSyncVisualViewportTop || typeof window === 'undefined') return;

    const visualViewport = window.visualViewport;
    if (!visualViewport) return;

    let rafId = 0;

    const syncVisualViewportTop = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setVisualViewportTop(Math.max(0, visualViewport.offsetTop || 0));
      });
    };

    visualViewport.addEventListener('resize', syncVisualViewportTop);
    visualViewport.addEventListener('scroll', syncVisualViewportTop);
    window.addEventListener('scroll', syncVisualViewportTop, {
      passive: true,
    });

    syncVisualViewportTop();

    return () => {
      cancelAnimationFrame(rafId);
      visualViewport.removeEventListener('resize', syncVisualViewportTop);
      visualViewport.removeEventListener('scroll', syncVisualViewportTop);
      window.removeEventListener('scroll', syncVisualViewportTop);
    };
  }, [shouldSyncVisualViewportTop]);

  const handleOnScroll = () => {
    const currentScrollY = window.scrollY;
    let offset = 10;
    if (currentScrollY > offset) {
      setFixedBarVisible(true);
    } else if (fixedBarVisible) {
      setFixedBarVisible(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll);
    return () => window.removeEventListener('scroll', handleOnScroll);
  }, [handleOnScroll]);

  const fixedBarStyle = useSpring({
    height: bannerHeight || (height ? height + 32 : 'auto'),
    config: { duration: shouldSyncVisualViewportTop ? 10 : 0 },
    top: fixedBarVisible ? nationalNotificationHeight + visualViewportTop : 0,
  });

  useEffect(() => {
    if (notificationActive && textValue) {
      dispatch(
        setNotificationHeight(bannerHeight || (height ? height + 32 : 0)),
      );
    } else {
      dispatch(setNotificationHeight(0));
    }
  }, [bannerHeight, height, notificationActive, textValue, dispatch]);

  useEffect(() => {
    if (!notificationActive || !textValue) {
      setBannerHeight(0);
      return undefined;
    }

    let rafId = 0;
    let timeoutId = 0;

    const syncBannerHeight = () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);

      rafId = requestAnimationFrame(measureBannerHeight);
      timeoutId = window.setTimeout(measureBannerHeight, 250);
    };

    syncBannerHeight();
    window.addEventListener('resize', syncBannerHeight, { passive: true });
    window.addEventListener('orientationchange', syncBannerHeight);
    window.visualViewport?.addEventListener('resize', syncBannerHeight);

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', syncBannerHeight);
      window.removeEventListener('orientationchange', syncBannerHeight);
      window.visualViewport?.removeEventListener('resize', syncBannerHeight);
    };
  }, [
    fixedBarVisible,
    height,
    measureBannerHeight,
    notificationActive,
    textValue,
  ]);

  if (!notificationActive || !textValue) {
    return null;
  }

  return ReactDOM.createPortal(
    <animated.div
      className={cx('z-40 bg-action-blue', {
        'relative ': !fixedBarVisible,
        'fixed left-0 right-0': fixedBarVisible,
      })}
      style={{
        ...fixedBarStyle,
        WebkitTransform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    >
      <BannerContainer
        ref={setBannerMeasureRef}
        style={{
          paddingTop:
            isIOS && fixedBarVisible && !nationalNotificationHeight
              ? 'env(safe-area-inset-top)'
              : undefined,
        }}
        className={cx(
          'w-screen flex items-start justify-between bg-action-blue py-4 z-50',
          {
            absolute: !!height,
          },
        )}
        role={'region'}
        onClick={(e) => {
          e.nativeEvent.stopPropagation();
          trackAnalyticsEvent({
            gtagEvent: 'NotifyBannerClick',
            mouseflowTag: 'NotifyBanner',
          });
        }}
        aria-label={'notification'}
      >
        <RichTextContainer
          className={cx('w-full text-white font-graphie', {
            'text-center': mBActive && mBAlignment === 'center',
            'text-right': mBActive && mBAlignment === 'right',
            'text-left': mBActive && mBAlignment === 'left',
          })}
        >
          <RichText
            tag='span'
            className='richtext'
            field={{ value: textValue }}
          ></RichText>
        </RichTextContainer>
        <button
          tabIndex={-1}
          className='mt-1'
          onClick={closeNotification}
          aria-label='Close notification banner'
          id='notification-banner-index'
        >
          <CloseIcon className='text-white w-6 h-6' />
        </button>
      </BannerContainer>
    </animated.div>,
    typeof window !== 'undefined' &&
      document.getElementById('notification-banner'),
  );
};

export default NotificationBanner;
